import type { MeasurementDimension, MeasurementMetric, WindowFunction } from '@kentik/ui-shared/query/query.types';
import type { AppProtocolRaw } from '@kentik/ui-shared/types/lookup.types';
import { AppProtocolsDictionary } from '@kentik/ui-shared/dictionary/types';
import { getAppProtocolsFromRaw } from '@kentik/ui-shared/dictionary/v2/compilers/appProtocols';
import { filterAppProtocols } from '@kentik/ui-shared/dictionary/v2/processors/appProtocols';

export interface AppProtocolMeasurementEntries {
  dimensions: Record<string, MeasurementDimension>;
  metrics: Record<string, MeasurementMetric>;
}

function parseLookupValues(lookup: Record<string, string> | undefined): Record<number, string> | undefined {
  if (!lookup) {
    return undefined;
  }
  const values: Record<number, string> = {};
  for (const [code, label] of Object.entries(lookup)) {
    const numCode = parseInt(code, 10);
    if (!Number.isNaN(numCode)) {
      values[numCode] = label;
    }
  }
  return Object.keys(values).length > 0 ? values : undefined;
}

/**
 * Transform raw app protocol data into MeasurementDimension and MeasurementMetric entries
 * that can be merged into the /traffic measurement's storage.
 */
export function getAppProtocolMeasurementEntries(appProtocols: AppProtocolRaw[]): AppProtocolMeasurementEntries {
  const dimensions: Record<string, MeasurementDimension> = {};
  const metrics: Record<string, MeasurementMetric> = {};

  for (const protocol of appProtocols) {
    const category = protocol.display_name;

    for (const col of protocol.columns) {
      const {
        custom_column,
        dimension_direction,
        dimension_label,
        metadata: { is_metric = false, hll, metric_type = 'sum', is_bytes, is_count }
      } = col;

      const colKey = `ktappprotocol__${protocol.app_protocol}__${custom_column}`;
      const chfColumn = custom_column.toLowerCase();

      if (is_metric || hll) {
        // Skip HLL metrics — they need generic aggregate (agg0) handling that UDE doesn't support yet
        if (!hll) {
          const metricEntry: MeasurementMetric = {
            Label: `${category}: ${dimension_label}`,
            Category: category,
            Column: chfColumn,
            Window: metric_type as WindowFunction,
            ToBits: is_bytes || undefined,
            Rollup: true,
            Shared: true,
            ...(is_count && chfColumn === 'i_count' && metric_type === 'sum' && { Alias: 'trautocount' })
          };

          const metricValues = parseLookupValues(col.metadata.lookup);
          if (metricValues) {
            metricEntry.Values = metricValues;
          }

          metrics[colKey] = metricEntry;
        }
      }

      if (!is_metric) {
        const dim: MeasurementDimension = {
          Label: dimension_label,
          Category: category,
          Column: chfColumn
        };

        if (dimension_direction === 'source') {
          dim.Direction = 'src';
          dim.Label = dimension_label.replace(/source\s/i, '');
        } else if (dimension_direction === 'destination') {
          dim.Direction = 'dst';
          dim.Label = dimension_label.replace(/destination\s/i, '');
        }

        const dimValues = parseLookupValues(col.metadata.lookup);
        if (dimValues) {
          dim.Values = dimValues;
        }

        dimensions[colKey] = dim;
      }
    }
  }

  return { dimensions, metrics };
}

export interface TransformedAppProtocols extends AppProtocolMeasurementEntries {
  app_protocols: AppProtocolsDictionary;
}

export function transformAppProtocols(raw: AppProtocolRaw[]): TransformedAppProtocols | null {
  if (!raw || raw.length === 0) {
    return null;
  }
  const filtered = filterAppProtocols({ appProtocols: raw, permissions: false });
  const { dimensions, metrics } = getAppProtocolMeasurementEntries(filtered);
  const app_protocols = getAppProtocolsFromRaw(filtered);
  return { dimensions, metrics, app_protocols };
}
